import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';

const AddTimeEntryModal = ({ show, onHide, onSave, employees, timeEntry, isEditing }) => {
  const today = new Date().toISOString().split('T')[0];
  
  const [formData, setFormData] = useState({
    employeeId: '',
    date: today,
    regularHours: '',
    overtimeHours: '0',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  
  // Initialize form data when editing
  useEffect(() => {
    if (isEditing && timeEntry) {
      setFormData({
        employeeId: timeEntry.EmployeeId.toString(),
        date: new Date(timeEntry.Date).toISOString().split('T')[0],
        regularHours: timeEntry.RegularHours.toString(),
        overtimeHours: (timeEntry.OvertimeHours || '0').toString(),
        notes: timeEntry.Notes || ''
      });
    } else {
      // Reset form when adding new entry
      setFormData({
        employeeId: employees.length === 1 ? employees[0].EmployeeId.toString() : '',
        date: today,
        regularHours: '',
        overtimeHours: '0',
        notes: ''
      });
    }
    
    setErrors({});
  }, [isEditing, timeEntry, employees, today]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For hours, format as decimal
    if (name === 'regularHours' || name === 'overtimeHours') {
      // Only allow numbers and decimal point
      const formattedValue = value.replace(/[^0-9.]/g, '');
      
      // Make sure it has at most one decimal point
      const parts = formattedValue.split('.');
      if (parts.length > 2) {
        return;
      }
      
      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        return;
      }
      
      setFormData({ ...formData, [name]: formattedValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  // Validate form inputs
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.employeeId) {
      newErrors.employeeId = 'Please select an employee';
    }
    
    if (!formData.date) {
      newErrors.date = 'Please select a date';
    }
    
    if (!formData.regularHours) {
      newErrors.regularHours = 'Please enter regular hours';
    } else if (isNaN(parseFloat(formData.regularHours)) || parseFloat(formData.regularHours) < 0) {
      newErrors.regularHours = 'Regular hours must be a positive number';
    }
    
    if (formData.overtimeHours && (isNaN(parseFloat(formData.overtimeHours)) || parseFloat(formData.overtimeHours) < 0)) {
      newErrors.overtimeHours = 'Overtime hours must be a positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Convert hours to numbers
      const formattedData = {
        ...formData,
        regularHours: parseFloat(formData.regularHours),
        overtimeHours: formData.overtimeHours ? parseFloat(formData.overtimeHours) : 0
      };
      
      onSave(formattedData);
    }
  };
  
  return (
    <Modal show={show} onHide={onHide} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Time Entry' : 'Add Time Entry'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Employee selection - only shown when creating new time entry */}
          {!isEditing && (
            <Form.Group className="mb-3">
              <Form.Label>Employee</Form.Label>
              <Form.Select
                name="employeeId"
                value={formData.employeeId}
                onChange={handleChange}
                isInvalid={!!errors.employeeId}
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp.EmployeeId} value={emp.EmployeeId}>
                    {emp.FirstName} {emp.LastName} - ${emp.HourlyWage}/hr
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.employeeId}
              </Form.Control.Feedback>
            </Form.Group>
          )}
          
          {/* When editing, show employee name */}
          {isEditing && timeEntry && (
            <div className="mb-3">
              <strong>Employee:</strong> {timeEntry.FirstName} {timeEntry.LastName}
            </div>
          )}
          
          <Form.Group className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              isInvalid={!!errors.date}
              max={today}
            />
            <Form.Control.Feedback type="invalid">
              {errors.date}
            </Form.Control.Feedback>
          </Form.Group>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Regular Hours</Form.Label>
                <Form.Control
                  type="text"
                  name="regularHours"
                  value={formData.regularHours}
                  onChange={handleChange}
                  isInvalid={!!errors.regularHours}
                  placeholder="0.00"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.regularHours}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Overtime Hours</Form.Label>
                <Form.Control
                  type="text"
                  name="overtimeHours"
                  value={formData.overtimeHours}
                  onChange={handleChange}
                  isInvalid={!!errors.overtimeHours}
                  placeholder="0.00"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.overtimeHours}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Overtime is calculated at 1.5x the hourly rate
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any notes about this time entry"
            />
          </Form.Group>
          
          {employees.length === 0 && (
            <Alert variant="warning">
              No employees are assigned to this project. You need to assign employees first.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={employees.length === 0}
          >
            {isEditing ? 'Update' : 'Add'} Time Entry
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

AddTimeEntryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  employees: PropTypes.array.isRequired,
  timeEntry: PropTypes.object,
  isEditing: PropTypes.bool
};

AddTimeEntryModal.defaultProps = {
  timeEntry: null,
  isEditing: false
};

export default AddTimeEntryModal;
